import { useMemo } from 'react';

import { trackEvent } from 'helpers/pawPrints';
import { createMutationHook, useApiQuery } from 'queries/common';
import { useSearchParams } from 'react-router-dom';

import internalAPI from '../helpers/internalAPI';
import { queryClient } from 'helpers/SPAWrapper';

export const ONBOARDING_TASKS = 'onboardingTasks';
const useOnboardingTasks = (params = {}, options = {}) => {
  const query = useApiQuery(
    [ONBOARDING_TASKS, 'list', params],
    () => internalAPI.get('/internal_api/v1/onboarding/tasks'),
    options,
  );

  return {
    ...query,
    data: query.data?.data,
  };
};

const completeOnboardingTasks = (params) =>
  internalAPI.post(
    `/internal_api/v1/onboarding/tasks/${params.slug}/complete`,
    params,
  );
const useCompleteOnboardingTasks = createMutationHook(completeOnboardingTasks);

const dismissOnboardingTasks = (params) =>
  internalAPI.post('/internal_api/v1/onboarding/dismiss', params);
const useDismissOnboardingTasks = createMutationHook(dismissOnboardingTasks);

const selectOnboardingIntegration = (params) =>
  internalAPI.put(`/internal_api/v1/onboarding/select_integration`, params);
const useSelectOnboardingIntegration = createMutationHook(
  selectOnboardingIntegration,
);

// New hook for fetching integrations status
const fetchIntegrationsStatus = () =>
  internalAPI.get(`/internal_api/v1/onboarding/integrations_status`);
const useIntegrationsStatus = (options = {}) => {
  const query = useApiQuery(
    ['integrationsStatus'],
    fetchIntegrationsStatus,
    options,
  );

  return {
    ...query,
    data: query.data?.data,
  };
};

const useOnboardingStep = (slug) => {
  const searchParams = useSearchParams()[0];
  const isSignalsOnboarding = searchParams.get('onboarding') === 'signals';
  const { data: onboardingTasks, isLoading } = useOnboardingTasks(
    {},
    {
      enabled: searchParams.get('onboarding') === 'signals',
    },
  );
  const onboardingTask = useMemo(() => {
    return onboardingTasks?.data?.tasks?.find((task) => task.id === slug);
  }, [onboardingTasks, slug]);

  const { mutate: completeOnboarding } = useCompleteOnboardingTasks();
  const completeOnboardingStep = () => {
    completeOnboarding({ slug });
    trackEvent({
      name: 'Signals',
      params: {
        page: 'User successfully complete step in setup list',
        event: slug,
      },
    });
    queryClient.invalidateQueries([ONBOARDING_TASKS]);
  };

  return {
    isSignalsOnboarding,
    completeOnboardingStep,
    onboardingTask,
    isLoading,
    isStepComplete: onboardingTask?.complete,
  };
};

export {
  useOnboardingTasks,
  useCompleteOnboardingTasks,
  useDismissOnboardingTasks,
  useSelectOnboardingIntegration,
  useIntegrationsStatus,
  useOnboardingStep,
};
